import {
  type AwardCriterionDto,
  type AwardCriterionType,
  type AwardSubCriterionDto,
  type LibraryElementContentDto,
  LibraryElementTypeDto,
  WeightType,
} from "../../autogen/bff-api";
import { SUB_WEIGHT_TOTAL } from "../award-criteria/awardCriteriaStatus";
import { libraryHostCriterionType } from "../procurement/publicProcurementOptions";
import { isUnweightedSet } from "./awardLibraryStatus";

/**
 * What an award criterion and its requirements look like once they are kept for reuse.
 *
 * A library element is a copy, not a link, and it belongs to no competition — so everything that only
 * meant something in the procurement it came from is left behind here rather than travelling with it.
 */

/** A requirement as it is kept inside a criterion: its weight stays, its id does not. */
export const toLibrarySubCriterion = (sub: Omit<AwardSubCriterionDto, "id">): AwardSubCriterionDto => ({
  name: sub.name,
  description: sub.description,
  weight: sub.weight,
  answerFormat: sub.answerFormat,
  requiresDocumentation: sub.requiresDocumentation,
});

/**
 * A criterion on its way into the library.
 *
 * The weight goes: it is this criterion's share of one competition, settled against the other criteria
 * on the table it was lifted off, and carrying it into the next procurement would only mislead. The id
 * goes for the same reason the other way round does (`useAddFromLibrary` strips it when a requirement
 * comes back out): the two are copies of each other, never the same row. `commonRationale` is buyer
 * -internal and the BFF drops it anyway; it is left out here so the payload says what it means.
 *
 * Sub-criteria come along with their weights — they are what makes a criterion worth keeping, and their
 * shares are measured against each other rather than against the competition, so they stay meaningful
 * wherever the criterion is reused. A requirement kept on its *own* is the case with no weight: alone it
 * has no siblings to be a share of, and `LibraryAwardSubCriterionDto` has no field for one.
 */
export const awardCriterionToLibraryContent = (criterion: AwardCriterionDto): LibraryElementContentDto => ({
  type: LibraryElementTypeDto.Award,
  award: {
    type: criterion.type,
    name: criterion.name,
    description: criterion.description,
    weight: undefined,
    subCriteria: (criterion.subCriteria ?? []).map(toLibrarySubCriterion),
  },
});

/**
 * A requirement on its way into the library, filed under the kind of criterion it was written in.
 *
 * `hostType` is the parent criterion's type as the event holds it, which is not always a type the
 * library will accept — see `libraryHostCriterionType`.
 */
export const awardSubCriterionToLibraryContent = (
  sub: Omit<AwardSubCriterionDto, "id">,
  hostType: AwardCriterionType
): LibraryElementContentDto => ({
  type: LibraryElementTypeDto.AwardSubCriterion,
  awardSubCriterion: {
    criterionType: libraryHostCriterionType(hostType),
    name: sub.name ?? "",
    description: sub.description,
    answerFormat: sub.answerFormat,
    requiresDocumentation: sub.requiresDocumentation,
  },
});

/**
 * An unweighted set of requirements given an even split of the criterion's 100 %.
 *
 * The library is allowed to hold a set nobody has weighted — there it is a definition (see
 * `isUnweightedSet`). A procurement is not: the weights have to total 100 before it can be
 * published, so the same criterion would otherwise land as a column of "Mangler vekt" for the buyer
 * to fill in by hand. With nothing to go on but the number of requirements, an even split is the
 * only answer available, so it is made rather than asked for.
 *
 * A *partly* weighted set is left alone. That is not an undecided set but an inconsistent one — the
 * wizard already flags it — and the numbers already there mean something to whoever typed them.
 */
export const withDistributedSubWeights = (criterion: AwardCriterionDto): AwardCriterionDto => {
  if (!isUnweightedSet(criterion)) return criterion;

  const subCriteria = criterion.subCriteria ?? [];
  const base = Math.floor(SUB_WEIGHT_TOTAL / subCriteria.length);
  // A weight of 0 is rejected by the form, the request validation and the domain type alike, so past
  // 100 requirements there is no split to make. Better the honest "missing weight" than a number
  // nothing downstream will accept.
  if (base < 1) return criterion;

  // Integer weights throughout — the whole form rounds them, and the sum is compared to 100 exactly,
  // so thirds have to come out as 34/33/33 rather than 33.33 three times.
  const remainder = SUB_WEIGHT_TOTAL - base * subCriteria.length;
  return {
    ...criterion,
    subCriteria: subCriteria.map((sub, index) => ({
      ...sub,
      weight: { number: index < remainder ? base + 1 : base, type: WeightType.Percentage },
    })),
  };
};

/**
 * A criterion on its way *out* of the library and onto a procurement or a template — the inverse of
 * `awardCriterionToLibraryContent`, and the only way a library criterion should be added to either.
 *
 * Fresh ids at both levels, for the reason `applyTemplate` gives for doing the same with a template:
 * two procurements adding the same library criterion must not share criterion or sub-criterion ids,
 * since evaluation scores and award-letter justifications are keyed by them. The library element and
 * the row are copies of each other, never the same row. Both mappers in the BFF mint an id when they
 * are given none, so leaving them off is what asks for a new one.
 */
export const fromLibraryCriterion = (award: AwardCriterionDto): AwardCriterionDto =>
  withDistributedSubWeights({
    ...award,
    id: undefined,
    // Left absent rather than emptied when there are none, so the patch says the same thing the
    // library element did.
    subCriteria: award.subCriteria?.map((sub) => ({ ...sub, id: undefined })),
  });
